import dayjs from 'dayjs';

export type TicketAvailabilityState = 'Available' | 'NotOnSale' | 'SalesEnded' | 'SoldOut' | 'NoTickets';

export type EventAvailabilityKind =
    | 'ended'
    | 'registrationClosed'
    | 'soldOut'
    | 'salesEnded'
    | 'notOnSale'
    | 'noTickets'
    | 'available'
    | 'unknown';

export interface EventAvailability {
    kind: EventAvailabilityKind;
    /** Short sentence-case status; empty when availability is unknown. */
    label: string;
    /** Longer explanation for detail surfaces. */
    description: string;
    tone: 'success' | 'warning' | 'error' | 'neutral';
    /** True only when ticket selection can lead to a purchase. */
    canBuy: boolean;
    /** Waitlist is only offered for genuinely sold-out events with registration still open. */
    canJoinWaitlist: boolean;
}

interface ResolveInput {
    ticketAvailability?: TicketAvailabilityState | null;
    registrationDeadline?: string | null;
    /** Event-local wall-clock end; only used when the API has not supplied `endUtc`. */
    endDate?: string | null;
    /** Real end instant (UTC ISO) from the API; preferred because `endDate` has no zone. */
    endUtc?: string | null;
    now?: dayjs.ConfigType;
}

/** Precedence: event ended, registration closed, then ticket state. A missing state is never guessed. */
export const resolveEventAvailability = ({ ticketAvailability, registrationDeadline, endDate, endUtc, now }: ResolveInput): EventAvailability => {
    const current = dayjs(now);

    if ((endUtc ?? endDate) && dayjs(endUtc ?? endDate).isBefore(current)) {
        return { kind: 'ended', label: 'Ended', description: 'This event has ended.', tone: 'neutral', canBuy: false, canJoinWaitlist: false };
    }

    const registrationOpen = registrationDeadline ? dayjs(registrationDeadline).endOf('day').isAfter(current) : true;
    if (!registrationOpen) {
        return { kind: 'registrationClosed', label: 'Registration closed', description: 'Registration for this event has closed.', tone: 'error', canBuy: false, canJoinWaitlist: false };
    }

    switch (ticketAvailability) {
        case 'Available':
            return { kind: 'available', label: 'Tickets available', description: 'Tickets are on sale.', tone: 'success', canBuy: true, canJoinWaitlist: false };
        case 'SoldOut':
            return { kind: 'soldOut', label: 'Sold out', description: 'All tickets have been sold.', tone: 'error', canBuy: false, canJoinWaitlist: true };
        case 'SalesEnded':
            return { kind: 'salesEnded', label: 'Sales ended', description: 'Ticket sales for this event have ended.', tone: 'error', canBuy: false, canJoinWaitlist: false };
        case 'NotOnSale':
            return { kind: 'notOnSale', label: 'Not on sale yet', description: 'Tickets for this event are not on sale yet.', tone: 'warning', canBuy: false, canJoinWaitlist: false };
        case 'NoTickets':
            return { kind: 'noTickets', label: 'No tickets available', description: 'There are no tickets available for this event.', tone: 'neutral', canBuy: false, canJoinWaitlist: false };
        default:
            return { kind: 'unknown', label: '', description: '', tone: 'neutral', canBuy: true, canJoinWaitlist: false };
    }
};
